import {
  AI_CHAT_INDEXED_DB_NAME,
  AI_CHAT_INDEXED_DB_STORE,
  AI_CHAT_INDEXED_DB_VERSION,
  MAX_PERSISTED_AI_CHAT_MESSAGES,
} from '@/config/aiChatPersistence';
import type {
  AiChatSurface,
  PersistedAiChatMessage,
  PersistedAiChatThread,
} from '@/types/ai-chat-persistence';

/**
 * Builds the primary key for a persisted AI chat thread.
 */
export function buildAiChatThreadId(
  userId: string,
  groupId: string,
  surface: AiChatSurface,
): string {
  return `${userId}:${groupId}:${surface}`;
}

/**
 * Keeps only the most recent messages up to the configured limit.
 */
export function trimPersistedMessages(
  messages: PersistedAiChatMessage[],
  maxMessages: number = MAX_PERSISTED_AI_CHAT_MESSAGES,
): PersistedAiChatMessage[] {
  if (messages.length <= maxMessages) {
    return messages;
  }
  return messages.slice(-maxMessages);
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openAiChatDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available'));
  }

  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(AI_CHAT_INDEXED_DB_NAME, AI_CHAT_INDEXED_DB_VERSION);
      request.onerror = () => reject(request.error ?? new Error('Failed to open AI chat database'));
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(AI_CHAT_INDEXED_DB_STORE)) {
          db.createObjectStore(AI_CHAT_INDEXED_DB_STORE, { keyPath: 'id' });
        }
      };
    });
  }

  return dbPromise;
}

function runTransaction<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openAiChatDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(AI_CHAT_INDEXED_DB_STORE, mode);
        const store = transaction.objectStore(AI_CHAT_INDEXED_DB_STORE);
        const request = run(store);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
      }),
  );
}

/**
 * Loads a persisted thread for the given scope, or null if none exists.
 */
export async function loadAiChatThread(
  userId: string,
  groupId: string,
  surface: AiChatSurface,
): Promise<PersistedAiChatThread | null> {
  const id = buildAiChatThreadId(userId, groupId, surface);
  try {
    const record = await runTransaction('readonly', (store) => store.get(id));
    return record ?? null;
  } catch {
    return null;
  }
}

/**
 * Upserts a thread after trimming messages to the persisted limit.
 */
export async function saveAiChatThread(thread: PersistedAiChatThread): Promise<void> {
  const payload: PersistedAiChatThread = {
    ...thread,
    messages: trimPersistedMessages(thread.messages),
    updatedAt: Date.now(),
  };

  try {
    await runTransaction('readwrite', (store) => store.put(payload));
  } catch {
    // Persistence is best-effort; chat must keep working offline of IDB failures.
  }
}

/**
 * Removes all locally persisted AI chat threads (e.g. on logout).
 */
export async function clearAllAiChatThreads(): Promise<void> {
  try {
    await runTransaction('readwrite', (store) => store.clear());
  } catch {
    // ignore
  }
}
